import React, { Component, PropTypes } from 'react';
import promiseRetry from 'promise-retry';
import { isEqual } from 'lodash';
import { media } from 'style-utils';
import styled from 'styled-components';

import InsightsApi from '../../../api/InsightsApi';

import {
  NitroLoading,
  NitroIcon,
  NitroText,
  NitroGrid,
  NitroHeading,
  NitroParagraph,
} from 'nitro-components';

import InsightTrend from './InsightTrend';
import InsightGauge from './InsightGauge';
import InsightUtilisation from './InsightUtilisation';
import InsightDriverSafety from './InsightDriverSafety';
import InsightPowerBI from './InsightPowerBI';
import Breakpoint from '../../../utils/breakpoints';
import { FormatErrorMsg } from '../../../interceptors/ApiInterceptor';

const InsightMetricContent = styled.div`
  margin-top: ${props => (props.type === 'tilePowerBI' ? 0 : 'auto')};
  ${props => {
    if (props.type === 'tilePowerBI') {
      return `
        height: 100%;
        div:nth-child(2){
          height: 100%;
        }
      `;
    }
  }}
  ${media.mobileTablet`
      margin-top: 0px;
    `};
`;

const InsightTrendLoader = styled.div`
  position: relative;
  height: 100%;
  min-height: 120px;
`;

const Tile = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  padding: ${props => (props.type === 'tileUtilisation' ? '0' : '24px')};
  padding-top: 24px;
  background: ${props => props.theme.color.white};
  box-shadow: ${props => props.theme.boxShadow[1]};
  border-radius: 2px;
  overflow: hidden;
  margin-bottom: 0;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  transition: all 0.2s ease;
  z-index: 2;

  &:hover {
    transform: translate(0, -6px);
    box-shadow: 0 12px 20px 0 rgba(0, 0, 0, 0.35);

    ${media.mobileTablet`
        transform: none;
        box-shadow: ${props => props.theme.boxShadow[1]};
      `};
  }
  ${media.mobile`
    z-index: 2;
    justify-content: space-around;

     > p {
      display: none;
     }
  `} ${media.averageMobile`
    padding: 12px;
  `}

  header {
    margin-top: 10px;
  }
`;

const TileHeading = styled.div`
  margin-top: 10px;

  ${media.mobile`
    min-height: 42px;
  `};
`;

const StyledNitroHeading = styled(NitroHeading)`
  font-size: ${props => props.theme.size[props.size]};

  ${media.mobile`
    font-size: ${props => props.theme.size[props.sizeMobile]};
  `};
`;

const NotificationBadge = styled.div`
  position: absolute;
  right: 2px;
  top: -6px;
  min-width: 26px;
  height: 25px;
  background: #ff632d;
  z-index: 3;
  padding: 2px 10px;
  color: ${props => props.theme.color.white};
  text-align: center;
  border-radius: 20px 10px 0px 20px;
  transition: all 0.2s ease;
  transform: ${props => (props.isHover ? 'translate(0, -6px)' : 'none')};

  ${media.tablet`transform:none;`} ${media.mobile`
    padding: 2px 8px;
    height: 25px;
    right: 0px;
    transform: none;
  `};
`;

const Description = styled.section`
  ${media.mobile`
   display: none;
  `};
`;

const NotificationBadgeBorder = styled.span`
  right: 2px;
  top: 19px;
  border: solid transparent;
  content: ' ';
  height: 0;
  width: 0;
  position: absolute;
  pointer-events: none;
  border-color: rgba(213, 129, 72, 0);
  border-top-color: #b83f16;
  border-width: 20px;
  margin-left: -15px;
  z-index: 1;
  transition: all 0.2s ease;
  transform: ${props => (props.isHover ? 'translate(0, -6px)' : 'none')};

  ${media.tablet`transform:none;`} ${media.mobile`
    top: 18px;
    right:0px;
     transform: none;
  `};
`;

const ErrorMsg = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 12px;
`;

class InsightMetric extends Component {
  constructor(props) {
    super(props);
    this.state = {
      dataType: '',
      metric: '',
      trends: [],
      values: [],
      description: '',
      trendType: '',
      loading: true,
      hoverState: false,
      error: {},
      timeStamp: '',
    };
    this.getData = this.getData.bind(this);
  }

  componentDidMount() {
    const { id, source, url } = this.props;
    if (source.country.value) {
      this.getData({ id, source, url });
    }
  }

  componentWillReceiveProps(props) {
    const { id, source, timeStamp } = props;

    if (this.state.timeStamp !== '' && timeStamp !== this.state.timeStamp) {
      this.getData({ id, source });
    }
    this.state.timeStamp = timeStamp;
  }

  componentWillUpdate(nextProps) {
    const { id, source, url } = nextProps;
    if (!isEqual(source, this.props.source)) {
      this.getData({ id, source, url });
    }
  }

  componentDidUpdate(nextProps) {
    const { id, source, url } = nextProps;
    if (
      nextProps.refreshContent &&
      nextProps.refreshContent !== this.props.refreshContent
    ) {
      this.getData({ id, source, url });
    }
  }
  componentWillUnmount() {
    InsightsApi.canCelTileData();
  }

  getData({ id, source, url }) {
    const { type } = this.props;

    if (type.toLowerCase() === 'tilepowerbi') {
      this.setState({
        loading: false,
      });
      return;
    }

    this.setState({
      loading: true,
    });

    // add promise retry logic for api Calls
    promiseRetry(
      (retry, number) =>
        InsightsApi.getTileData(
          // uncomment below code to test retry logic
          // id !== '32362709-812f-4c93-a5c4-8c1916abc6a1' ? id : 'zxcvx',
          id,
          source,
          url
        ).catch(retry),
      // limit number of retries to 2
      { retries: 2 }
    ).then(
      data => {
        if (data) {
          if (!this.loader) {
            return;
          }
          this.setState({
            metric: data.Data,
            trends: data.Trends,
            values: data.Values,
            dataType: data.DataType,
            description: data.Description,
            delta: data.Delta,
            trendType: data.DeltaType,
            loading: false,
            notificationCount: data.NotificationCount,
            showNotification: false,
          });
        } else {
          console.log('InsightsApi.getTileData error');
        }
      },
      error => {
        this.setState({
          loading: false,
          showNotification: false,
          error,
        });
        console.log('InsightsApi.getTileData error', error);
      }
    );
  }

  toggleHover = () => {
    this.setState({
      hoverState: true,
    });
  };

  toggleOut = () => {
    this.setState({
      hoverState: false,
    });
  };

  render() {
    const { type, description, title, route, url, id } = this.props;
    let MetricComponent;
    switch (type) {
      case 'tileGauge':
        MetricComponent = InsightGauge;
        break;
      case 'tileUtilisation':
        MetricComponent = InsightUtilisation;
        break;
      case 'tileDriverSafety':
        MetricComponent = InsightDriverSafety;
        break;
      case 'tilePowerBI':
        MetricComponent = InsightPowerBI;
        break;
      default:
        MetricComponent = InsightTrend;
        break;
    }

    if (this.state.loading) {
      return (
        <InsightTrendLoader
          ref={ref => {
            this.loader = ref;
          }}
        >
          <NitroLoading center />
        </InsightTrendLoader>
      );
    }

    return (
      <div>
        {this.state.notificationCount > 0 && this.state.showNotification && (
          <section>
            <NotificationBadge isHover={this.state.hoverState}>
              {' '}
              <NitroIcon
                name="notifications"
                size="14"
                variant="white"
                right={6}
              />
              <Breakpoint name="tabletDesktop">
                <NitroText size="4">
                  <NitroText size="4" bold>
                    {this.state.notificationCount}
                  </NitroText>{' '}
                  {this.state.notificationCount > 1 ? 'alerts' : ' alert'}
                </NitroText>
              </Breakpoint>
              <Breakpoint name="mobile">
                <NitroText size="4" bold>
                  {this.state.notificationCount}
                </NitroText>
              </Breakpoint>
            </NotificationBadge>
            <NotificationBadgeBorder isHover={this.state.hoverState} />
          </section>
        )}

        <NitroGrid square>
          <Tile
            onMouseEnter={this.toggleHover}
            onMouseLeave={this.toggleOut}
            type={type}
          >
            {type !== 'tilePowerBI' && (
              <TileHeading>
                <StyledNitroHeading
                  label={title}
                  size="3"
                  sizeMobile="4"
                  variant="secondary"
                  align="center"
                  noMargin
                />
                <Description>
                  <NitroParagraph align="center" size="4" noMargin>
                    {description}
                  </NitroParagraph>
                </Description>
              </TileHeading>
            )}
            <InsightMetricContent type={type}>
              <MetricComponent
                {...this.state}
                route={route}
                url={url}
                id={id}
              />
              {this.state.error && (
                // <ErrorMsg>{FormatErrorMsg(this.state.error)}</ErrorMsg>
                // TODO - Put generic error message for now
                <ErrorMsg>
                  {FormatErrorMsg(this.state.error) &&
                    'Oops, something went wrong '}
                </ErrorMsg>
              )}
            </InsightMetricContent>
          </Tile>
        </NitroGrid>
      </div>
    );
  }
}

InsightMetric.propTypes = {
  id: PropTypes.string,
  source: PropTypes.object,
  type: PropTypes.string,
  title: PropTypes.string,
  description: PropTypes.string,
  onClick: PropTypes.func,
  notificationData: PropTypes.array,
  deleteComplete: PropTypes.bool,
  refreshContent: PropTypes.bool,
  route: PropTypes.string,
  url: PropTypes.string,
};

export default InsightMetric;



// WEBPACK FOOTER //
// ./src/features/insights/components/InsightMetric.js